import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { AppService, GetAllReferralsByMemberRequest, GetAllReferralsByMemberResponse, ReferralSummary } from '../../app.service';
import { AlertService } from '../alert.service';
import { LoadingService } from '../loading.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { finalize } from 'rxjs';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { ReferralStatusDialogComponent, ReferralStatusDialogData, ReferralStatusResult } from '../referral-status-dialog/referral-status-dialog.component';
import { MatDialog } from '@angular/material/dialog';


@Component({
  selector: 'app-member-referral-history',
  templateUrl: './member-referral-history.component.html',
  styleUrls: ['./member-referral-history.component.scss'] // <-- plural!
})
export class MemberReferralHistoryComponent implements OnInit, OnChanges {

  @Input() public memberId!: string;
  @Output() totalChange = new EventEmitter<number>();
  @Output() refreshRequested = new EventEmitter<boolean>();

  query = '';
  

  displayedColumns = ['created', 'source', 'dco', 'status', 'actions'];
  dataSource = new MatTableDataSource<ReferralSummary>([]);
  total = 0;

  /** UI state */
  includeReprocess = false;
  sourceFilter: 'ALL' | 'PCP' | 'HRAT' | 'Maternity' = 'ALL';

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;

      //// Defer to next microtask to avoid changing aria-sort mid-check
      //queueMicrotask(() => {
      //  if (!this.dataSource.sort) return;

      //  // Newer API to set sort programmatically (Angular Material 15+)
      //  sort.sort({ id: 'created', start: 'desc', disableClear: true });

      //  // If you use OnPush, ensure the view sees this:
      //  this.cdr.markForCheck();
      //});
    }
  }

  constructor(
    private service: AppService,
    private alert: AlertService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (this.memberId) this.load();
    this.setupFiltering();
    this.setupSortingAccessor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['memberId'] && !changes['memberId'].firstChange && this.memberId) {
      this.load();
    }
  }

  private setupSortingAccessor() {
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'created') return new Date(item.createdUtc).getTime();
      if (item.isHidden) {
        // Only date should influence sorting for hidden rows
        switch (property) {
          case 'source':
          case 'dco':
          case 'status':
            return '';
        }
      }
      switch (property) {
        case 'source': return item.sourceType;
        case 'dco': return item.dcoName ?? '';
        case 'status': return (item.isClosed ? '1' : '0') + (item.isUrgent ? '1' : '0') + (item.isInReprocess ? '1' : '0');
        default: return (item as any)[property];
      }
    };
  }

  toggleReprocess() {
    this.includeReprocess = !this.includeReprocess;
    this.applyFilter();
  }

  setSourceFilter(ev: MatButtonToggleChange) {
    this.sourceFilter = ev.value as any;
    this.applyFilter();
  }

  load() {
    if (!this.memberId) return;

    const req: GetAllReferralsByMemberRequest = { memberMedicaidId: this.memberId };

    // Capture current sort state (or fall back to created/desc once)
    const prevActive = this.dataSource.sort?.active || 'created';
    const prevDir = (this.dataSource.sort?.direction as 'asc' | 'desc') || 'desc';

    this.service.GetAllReferralsByMember(req).subscribe({
      next: (res) => {
        if (!res.success) {
          this.alert.error(res.message || 'Failed to load referral history.');
          this.dataSource.data = [];
          this.updateCounts();
          return;
        }

        this.dataSource.data = res.referrals ?? [];
        this.updateCounts();
        this.applyFilter();

        // Reapply SAME sort on the next microtask (prevents header auto-toggling)
        //queueMicrotask(() => {
        //  const sort = this.dataSource.sort;
        //  if (sort) {
        //    sort.sort({ id: prevActive, start: 'desc', disableClear: true });
        //  }
        //});
      },
      error: (err) => {
        console.error(err);
        this.alert.error('An unexpected error occurred loading referral history.');
        this.dataSource.data = [];
        this.updateCounts();
      }
    });
  }


  private updateCounts() {
    this.total = this.dataSource.data.length;
    this.totalChange.emit(this.total);    
  }

  onQuery(val: string) {
    this.query = (val || '').toLowerCase();
    this.applyFilter();
  }

  private setupFiltering() {
    this.dataSource.filterPredicate = (row, filterStr) => {
      const f = JSON.parse(filterStr || '{}') as {
        includeReprocess: boolean;
        source: 'ALL' | 'PCP' | 'HRAT' | 'Maternity';
        q: string;
      };

      const reprocessOk = f.includeReprocess || !row.isInReprocess;
      const sourceOk = f.source === 'ALL' || row.sourceType === f.source;
      if (!(reprocessOk && sourceOk)) return false;

      // Hidden rows: only allow date to be searchable; otherwise they always show
      if (row.isHidden) {
        if (!f.q) return true;
        const dateStr = new Date(row.createdUtc).toLocaleString().toLowerCase();
        return dateStr.includes(f.q);
      }

      const hay = [
        row.dcoName ?? '',
        row.sourceType,
        row.isClosed ? 'closed' : 'open',
        row.isUrgent ? 'urgent' : '',
        row.isInReprocess ? 'reprocess' : ''
      ].join(' ').toLowerCase();

      return !f.q || hay.includes(f.q);
    };

    this.applyFilter();
  }

  private applyFilter() {
    const f = { includeReprocess: this.includeReprocess, source: this.sourceFilter, q: this.query };
    this.dataSource.filter = JSON.stringify(f);
  }

  openReferralStatusDialog(r: ReferralSummary) {
    if (r.isHidden) return;
    const data: ReferralStatusDialogData = {
      sourceType: r.sourceType,
      referralId: r.referralId,
      isClosed: r.isClosed,
      currentDcoNote: '' // if you have this on the row; otherwise null is fine
    };

    const ref = this.dialog.open(ReferralStatusDialogComponent, {
      width: '520px',
      autoFocus: 'first-tabbable',
      data
    });

    ref.afterClosed().subscribe((result?: ReferralStatusResult) => {
      if (!result) return; // cancelled
      if (result.saved) {
        this.alert.success('Referral updated.');
        this.load(); // refresh the grid
        this.refreshRequested.emit(true);
      }
    });
  }
}
